import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { Loading, PriorityBadge, EmptyState, PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ClipboardList, AlertTriangle, Activity, CalendarClock, FileSearch, CheckCircle2 } from "lucide-react";

const selectCls = "h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic transition-colors";
const tabCls = "data-[state=active]:bg-brand-navy data-[state=active]:text-white text-slate-500";

export default function Assignments() {
  const { user } = useAuth();
  const isManager = hasMinRole(user?.role, "manager");
  return (
    <div>
      <PageHeader title="Assignments" subtitle="Track and manage legislative review work across the team." />
      <Tabs defaultValue="my">
        <TabsList className="bg-white border border-brand-line" data-testid="assignments-tabs">
          <TabsTrigger value="my" data-testid="tab-my" className={tabCls}>My Assignments</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all" className={tabCls}>All</TabsTrigger>
          {isManager && <TabsTrigger value="team" data-testid="tab-team" className={tabCls}>Team Assignments</TabsTrigger>}
        </TabsList>
        <TabsContent value="my" className="mt-4"><MyAssignments /></TabsContent>
        <TabsContent value="all" className="mt-4"><AllAssignments /></TabsContent>
        {isManager && <TabsContent value="team" className="mt-4"><TeamAssignments /></TabsContent>}
      </Tabs>
    </div>
  );
}

function AssignmentRow(a, update) {
  return (
    <div key={a.assignment_id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4" data-testid={`assignment-${a.assignment_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/bills/${a.bill_id}`} className="font-mono font-semibold text-brand-civic hover:text-brand-navy hover:underline">{a.bill?.bill_number}</Link>
          {a.bill && <PriorityBadge priority={a.bill.priority} />}
          {a.is_primary && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-gold-50 text-[#9A7212] font-bold uppercase">Primary</span>}
          <span className="text-xs text-slate-500 capitalize">{a.assignment_role} · {a.user?.name}</span>
        </div>
        <p className="text-sm text-slate-700 truncate mt-0.5">{a.bill?.title}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">Due {fmtDate(a.due_date)}</span>
        <select value={a.status} onChange={(e) => update(a.assignment_id, { status: e.target.value })} data-testid={`assignment-status-${a.assignment_id}`} className={selectCls}>
          {["assigned", "in_progress", "completed", "blocked"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select value={a.review_status} onChange={(e) => update(a.assignment_id, { review_status: e.target.value })} data-testid={`assignment-review-${a.assignment_id}`} className={selectCls}>
          {["pending", "under_review", "approved", "rejected"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      </div>
    </div>
  );
}

const BUCKETS = [
  ["needs_attention", "Needs Attention", AlertTriangle, "text-red-500"],
  ["recently_changed", "Recently Changed", Activity, "text-brand-civic"],
  ["upcoming_hearing", "Upcoming Hearing", CalendarClock, "text-brand-gold"],
  ["pending_analysis", "Pending My Analysis", FileSearch, "text-brand-navy"],
  ["pending_review", "Pending Review", ClipboardList, "text-purple-500"],
  ["completed", "Completed", CheckCircle2, "text-brand-teal"],
];

function MyAssignments() {
  const [data, setData] = useState(null);
  const load = () => { setData(null); api.get("/assignments/my-summary").then((r) => setData(r.data)).catch(() => setData(false)); };
  useEffect(load, []);
  const update = async (id, patch) => { try { await api.put(`/assignments/${id}`, patch); toast.success("Updated"); load(); } catch { toast.error("Update failed"); } };
  if (data === null) return <Loading />;
  if (!data) return <EmptyState icon={ClipboardList} title="Unable to load assignments" />;
  const total = Object.values(data.counts).reduce((x, y) => x + y, 0);
  if (total === 0) return <EmptyState icon={ClipboardList} title="No assignments" description="Bills assigned to you will appear here, grouped by what needs your attention." />;
  return (
    <div className="space-y-5" data-testid="my-assignments">
      {BUCKETS.map(([key, label, Icon, color]) => {
        const items = data.buckets[key] || [];
        if (!items.length) return null;
        return (
          <div key={key} data-testid={`bucket-${key}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className={`h-4 w-4 ${color}`} />
              <h3 className="font-display font-semibold text-brand-ink">{label}</h3>
              <span className="text-xs font-bold text-slate-400">{items.length}</span>
            </div>
            <div className="rounded-xl border border-brand-line bg-white divide-y divide-brand-line shadow-card">
              {items.map((a) => AssignmentRow(a, update))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AllAssignments() {
  const [data, setData] = useState(null);
  const load = () => { setData(null); api.get("/assignments").then((r) => setData(r.data.assignments)).catch(() => setData(false)); };
  useEffect(load, []);
  const update = async (id, patch) => { try { await api.put(`/assignments/${id}`, patch); toast.success("Updated"); load(); } catch { toast.error("Update failed"); } };
  if (data === null) return <Loading />;
  if (!data || data.length === 0) return <EmptyState icon={ClipboardList} title="No assignments" />;
  return <div className="rounded-xl border border-brand-line bg-white divide-y divide-brand-line shadow-card" data-testid="assignments-list">{data.map((a) => AssignmentRow(a, update))}</div>;
}

function TeamAssignments() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/assignments/team-summary").then((r) => setData(r.data.team)).catch(() => setData(false)); }, []);
  if (data === null) return <Loading />;
  if (!data || data.length === 0) return <EmptyState icon={ClipboardList} title="No team assignments" description="Assign bills to staff to see workload here." />;
  const cols = [["open", "Open"], ["priority", "Priority"], ["pending_analysis", "Pending Analysis"], ["pending_review", "Pending Review"], ["overdue", "Overdue"], ["recent_changes", "Recent Changes"]];
  return (
    <div className="rounded-xl border border-brand-line bg-white shadow-card overflow-x-auto" data-testid="team-assignments">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-brand-line text-left">
            <th className="px-4 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Staff</th>
            {cols.map(([, label]) => <th key={label} className="px-4 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-semibold text-center">{label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-brand-line">
          {data.map((r) => (
            <tr key={r.user.user_id} data-testid={`team-row-${r.user.user_id}`} className="hover:bg-slate-50">
              <td className="px-4 py-3"><div className="font-medium text-brand-ink">{r.user.name}</div><div className="text-xs text-slate-400 capitalize">{r.user.role}</div></td>
              {cols.map(([k]) => (
                <td key={k} className="px-4 py-3 text-center">
                  <span className={`font-semibold ${k === "overdue" && r[k] > 0 ? "text-red-500" : k === "priority" && r[k] > 0 ? "text-[#9A7212]" : "text-brand-ink"}`}>{r[k]}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
